// Types pour le module Comptabilité KOPRO
// Basés sur les types Supabase (source de vérité)

import type { Row, Insert, Update, Enums } from '@/shared/types/database'

// ===== ENUMS (depuis Supabase) =====
export type AccountType = Enums<'account_type'>
export type DistributionCalculationBase = Enums<'distribution_calculation_base'>
export type BudgetStatus = Enums<'budget_status'>
export type FundCallStatus = Enums<'fund_call_status'>
export type TransactionStatus = Enums<'transaction_status'>
export type PayerType = Enums<'payer_type'>
export type AtypicalMovementType = Enums<'atypical_movement_type'>
export type FundCallType = Enums<'fund_call_type'>
export type ChargePayerType = Enums<'charge_payer_type'>

// Types not in enums (stored as string in DB)
export type FiscalYearStatus = 'draft' | 'open' | 'closed'
export type BudgetType = 'operating' | 'works' | 'reserve'
export type BudgetCallFrequency = Enums<'budget_call_frequency'>

// Labels pour les types de budget (conformes à la réglementation)
export const BUDGET_TYPE_LABELS: Record<BudgetType, string> = {
  operating: 'Charges courantes',
  works: 'Travaux (art. 606)',
  reserve: 'Fonds de travaux ALUR (art. 14-2 loi 10/07/1965)',
}

// Règles d'imputation selon le type de propriété (réglementation)
// - Usufruitiers : uniquement charges courantes
// - Nus-propriétaires : uniquement travaux et fonds ALUR
// - Pleins propriétaires : tous les budgets
export const BUDGET_TYPE_BY_OWNERSHIP: Record<BudgetType, ('full' | 'usufruct' | 'bare')[]> = {
  operating: ['full', 'usufruct'],      // Charges courantes → PP + USU
  works: ['full', 'bare'],              // Travaux → PP + NP
  reserve: ['full', 'bare'],            // Fonds ALUR → PP + NP
}

// ===== EXERCICES COMPTABLES =====
export type FiscalYear = Row<'fiscal_years'>
export type FiscalYearInsert = Insert<'fiscal_years'>
export type FiscalYearUpdate = Update<'fiscal_years'>

// Alias pour compatibilité
export type FiscalYearCreate = Omit<FiscalYearInsert, 'id' | 'created_at' | 'updated_at'>

// ===== PLAN COMPTABLE =====
export type ChartAccount = Row<'chart_accounts'>
export type ChartAccountInsert = Insert<'chart_accounts'>
export type ChartAccountUpdate = Update<'chart_accounts'>

export type ResidenceChartAccount = Row<'residence_chart_accounts'> & {
  chart_account?: ChartAccount
  distribution_key?: Pick<DistributionKey, 'id' | 'name' | 'key_type'> | null
}
export type ResidenceChartAccountInsert = Insert<'residence_chart_accounts'>
export type ResidenceChartAccountUpdate = Update<'residence_chart_accounts'>

// ===== CLÉS DE RÉPARTITION =====
export type DistributionKeyType = 'general' | 'special'

export type DistributionKey = Row<'distribution_keys'> & {
  key_type: DistributionKeyType
}
export type DistributionKeyInsert = Insert<'distribution_keys'>
export type DistributionKeyUpdate = Update<'distribution_keys'>

export type DistributionKeyUnit = Row<'distribution_key_units'> & {
  unit?: {
    id: string
    unit_number: string
    stairwell?: string | null
    floor?: number | null
  }
}
export type DistributionKeyUnitInsert = Insert<'distribution_key_units'>
export type DistributionKeyUnitUpdate = Update<'distribution_key_units'>

export interface DistributionResult {
  unit_id: string
  owner_id: string
  share_value: number
  share_percentage: number
  amount: number
}

// ===== CLÉS VARIABLES & RELEVÉS DE COMPTEURS =====
// Use Supabase generated enum types
export type MeterType = Enums<'meter_type'>
export type MeterReadingFrequency = Enums<'meter_reading_frequency'>
export type MissingReadingAllocationMethod = Enums<'missing_reading_allocation_method'>

export type MeterReading = Row<'meter_readings'> & {
  unit?: {
    id: string
    unit_number: string
    stairwell?: string | null
    floor?: number | null
  }
  fiscal_year?: {
    id: string
    year: number
  }
}
export type MeterReadingInsert = Insert<'meter_readings'>
export type MeterReadingUpdate = Update<'meter_readings'>

// Résultat de calcul variable enrichi
export interface VariableDistributionResult {
  unit_id: string
  unit_number: string
  consumption: number
  total_consumption: number
  percentage: number
  amount: number
  is_estimated: boolean
  allocation_method: string
}

// Labels et helpers pour les clés variables
export const METER_TYPE_LABELS: Record<MeterType, string> = {
  water_cold: 'Eau froide',
  water_hot: 'Eau chaude',
  gas: 'Gaz',
  electricity: 'Électricité',
  heating: 'Chauffage collectif',
  other: 'Autre',
}

export const METER_TYPE_UNITS: Record<MeterType, string> = {
  water_cold: 'm³',
  water_hot: 'm³',
  gas: 'm³',
  electricity: 'kWh',
  heating: 'kWh',
  other: 'unités',
}

export const MISSING_READING_METHOD_LABELS: Record<MissingReadingAllocationMethod, string> = {
  shares: 'Tantièmes généraux',
  surface: 'Surface des lots',
  equal: 'Parts égales',
  previous_consumption: 'Consommation année précédente',
}

export const METER_READING_FREQUENCY_LABELS: Record<MeterReadingFrequency, string> = {
  monthly: 'Mensuelle',
  quarterly: 'Trimestrielle',
  biannual: 'Semestrielle',
  annual: 'Annuelle',
}

// Labels pour la périodicité des appels de fonds sur les budgets
export const BUDGET_CALL_FREQUENCY_LABELS: Record<BudgetCallFrequency, string> = {
  monthly: 'Mensuel (12 appels/an)',
  quarterly: 'Trimestriel (4 appels/an)',
  semi_annual: 'Semestriel (2 appels/an)',
  annual: 'Annuel (1 appel/an)',
}

export const CALLS_PER_YEAR: Record<BudgetCallFrequency, number> = {
  monthly: 12,
  quarterly: 4,
  semi_annual: 2,
  annual: 1,
}

// ===== BUDGETS (LOT 2) =====
export type Budget = Row<'budgets'> & {
  fiscal_year?: FiscalYear
  budget_lines?: BudgetLine[]
}
export type BudgetInsert = Insert<'budgets'>
export type BudgetUpdate = Update<'budgets'>
export type BudgetCreate = Omit<BudgetInsert, 'id' | 'created_at' | 'updated_at'>

// Le compte de charges est déterminé automatiquement par le type de budget :
// - operating: 701 (Provisions sur charges)
// - works: 102 (Travaux art. 606)
// - reserve: 105 (Fonds travaux ALUR)
export type BudgetLine = Row<'budget_lines'> & {
  distribution_key?: DistributionKey
}
export type BudgetLineInsert = Insert<'budget_lines'>
export type BudgetLineUpdate = Update<'budget_lines'>
export type BudgetLineCreate = Omit<BudgetLineInsert, 'id' | 'created_at' | 'updated_at'>

// Mapping des types de budget vers les codes de compte
export const BUDGET_TYPE_ACCOUNT_CODES: Record<BudgetType, string> = {
  operating: '701', // Provisions sur charges
  works: '102',     // Travaux art. 606
  reserve: '105',   // Fonds travaux ALUR
}

// ===== APPELS DE FONDS (LOT 2) =====

// Table de jonction fund_call_budgets (multi-budget)
export type FundCallBudget = Row<'fund_call_budgets'> & {
  budget?: Budget
}
export type FundCallBudgetInsert = Insert<'fund_call_budgets'>
export type FundCallBudgetUpdate = Update<'fund_call_budgets'>

export type FundCall = Row<'fund_calls'> & {
  fiscal_year?: FiscalYear
  /** @deprecated Utiliser fund_call_budgets pour les associations multi-budgets */
  budget?: Budget
  fund_call_budgets?: FundCallBudget[]
  fund_call_items?: FundCallItem[]
}
export type FundCallInsert = Insert<'fund_calls'>
export type FundCallUpdate = Update<'fund_calls'>
export type FundCallCreate = Omit<FundCallInsert, 'id' | 'created_at' | 'updated_at'>

export type FundCallItem = Row<'fund_call_items'> & {
  owner?: {
    id: string
    first_name: string
    last_name: string
    email?: string
  }
  unit?: {
    id: string
    unit_number: string
    stairwell?: string
  }
  fund_call_item_details?: FundCallItemDetail[]
}
export type FundCallItemInsert = Insert<'fund_call_items'>
export type FundCallItemUpdate = Update<'fund_call_items'>

// Détail des calculs pour une ligne d'appel de fonds
export type FundCallItemDetail = Row<'fund_call_item_details'> & {
  budget_line?: BudgetLine
  distribution_key?: DistributionKey
}
export type FundCallItemDetailInsert = Insert<'fund_call_item_details'>

// ===== PAIEMENTS COPROPRIÉTAIRES (LOT 3) =====
export type OwnerPayment = Row<'owner_payments'> & {
  payer_owner?: {
    id: string
    first_name: string
    last_name: string
    email?: string
  }
  fiscal_year?: FiscalYear
}
export type OwnerPaymentInsert = Insert<'owner_payments'>
export type OwnerPaymentUpdate = Update<'owner_payments'>

export type PaymentDistribution = Row<'payment_distributions'> & {
  beneficiary_owner?: {
    id: string
    first_name: string
    last_name: string
  }
  unit?: {
    id: string
    unit_number: string
  }
  fund_call_item?: FundCallItem
}
export type PaymentDistributionInsert = Insert<'payment_distributions'>

export type AtypicalMovement = Row<'atypical_movements'> & {
  source_owner?: {
    id: string
    first_name: string
    last_name: string
  }
  target_owner?: {
    id: string
    first_name: string
    last_name: string
  }
  source_unit?: {
    id: string
    unit_number: string
  }
  target_unit?: {
    id: string
    unit_number: string
  }
}
export type AtypicalMovementInsert = Insert<'atypical_movements'>

// Vue des soldes copropriétaires (calculée depuis les écritures comptables)
export interface OwnerBalance {
  owner_id: string | null
  first_name: string | null
  last_name: string | null
  residence_id: string
  residence_name: string
  unit_id: string
  unit_number: string
  account_id: string | null
  account_code: string
  // Type de propriété
  ownership_type: 'full' | 'usufruct' | 'bare' | null
  share_numerator: number | null
  share_denominator: number | null
  // Soldes
  total_debit: number
  total_credit: number
  balance: number
  unpaid_amount: number
}

// Labels pour les types de propriété
export const OWNERSHIP_TYPE_LABELS: Record<string, string> = {
  full: 'Pleine propriété',
  usufruct: 'Usufruit',
  bare: 'Nue-propriété',
}

// Labels courts pour l'affichage dans les tableaux
export const OWNERSHIP_TYPE_SHORT_LABELS: Record<string, string> = {
  full: 'PP',
  usufruct: 'USU',
  bare: 'NP',
}

// ===== TRANSACTIONS / ÉCRITURES (LOT 3) =====
export type Journal = Row<'journals'>

export type Transaction = Row<'transactions'> & {
  journal?: Journal
  lines?: TransactionLine[]
}
export type TransactionInsert = Insert<'transactions'>
export type TransactionUpdate = Update<'transactions'>

export type TransactionLine = Row<'transaction_lines'> & {
  account?: ChartAccount
  owner?: {
    id: string
    first_name: string
    last_name: string
  }
  unit?: {
    id: string
    unit_number: string
  }
}
export type TransactionLineInsert = Insert<'transaction_lines'>
export type TransactionLineUpdate = Update<'transaction_lines'>

export interface TransactionCreate {
  journal_id: string
  residence_id: string
  fiscal_year_id: string
  transaction_date: string
  description: string
  reference?: string
  lines: TransactionLineCreate[]
}

export interface TransactionLineCreate {
  residence_chart_account_id: string
  debit: number
  credit: number
  label?: string
  owner_id?: string
  unit_id?: string
  fund_call_item_id?: string
  payment_id?: string
}

// ===== RAPPROCHEMENT BANCAIRE =====
export interface BankReconciliation {
  statement_id: string
  transaction_line_id: string
  reconciled_at: string
  reconciled_by: string
}

// ===== ÉTATS FINANCIERS =====
export interface FinancialReport {
  type: 'annexe1' | 'annexe2' | 'annexe3' | 'annexe4' | 'balance' | 'grand_livre'
  fiscal_year_id: string
  residence_id: string
  generated_at: string
  data: Record<string, unknown>
}

// ===== FILTRES ET RECHERCHE =====
export interface AccountingFilters {
  residence_id?: string
  fiscal_year_id?: string
  date_from?: string
  date_to?: string
  status?: string
  search?: string
}

export interface TransactionFilters extends AccountingFilters {
  journal_id?: string
  account_code?: string
  owner_id?: string
}

export interface FundCallFilters extends AccountingFilters {
  budget_id?: string
  call_status?: FundCallStatus
}

// ===== LETTRAGE =====
export interface Lettrage {
  id: string
  residence_id: string
  residence_chart_account_id: string
  display_code: string
  lettrage_date: string
  created_by: string | null
  created_at: string
  lines?: TransactionLine[]
}

export interface LettrageCreate {
  residence_chart_account_id: string
  line_ids: string[]
}

// Statut de lettrage d'une ligne
export type LettrageStatus = 'unlettered' | 'partially_lettered' | 'fully_lettered'

// Ligne d'écriture pour le lettrage (vue enrichie)
export interface LettrageEntryLine {
  id: string
  transaction_id: string
  account_id: string
  debit: number
  credit: number
  label: string | null
  // Nouveau système de lettrage par montant
  lettered_amount: number // Montant déjà lettré
  remaining_amount: number // Montant restant à lettrer
  lettrage_status: LettrageStatus
  lettrage_codes: string[] // Codes des lettrages (peut être multiple)
  // Anciens champs pour compatibilité (deprecated)
  /** @deprecated Utiliser lettrage_status à la place */
  lettrage_id: string | null
  /** @deprecated Utiliser lettrage_codes à la place */
  lettrage_code: string | null
  // Données jointes
  transaction_date: string
  description: string
  piece_number: string | null
  reference: string | null
  account_code: string
  account_name: string
  owner_id: string | null
  owner_name: string | null
  unit_id: string | null
  unit_number: string | null
}

// Statistiques d'un compte pour le lettrage
export interface AccountLettrageStats {
  account_id: string
  account_code: string
  account_name: string
  owner_id: string | null
  owner_name: string | null
  unit_id: string | null
  unit_number: string | null
  total_debit: number
  total_credit: number
  // Solde réel = crédits - débits (négatif = débiteur, positif = créditeur)
  balance: number
  unlettered_debit: number
  unlettered_credit: number
  lettered_percentage: number
  line_count: number
  unlettered_line_count: number
}

// Suggestion de lettrage
export interface MatchSuggestion {
  debit_id: string
  credit_ids: string[]
  confidence: number
  reason: string
}

// État de connexion pour l'UI
export type ConnectionState = 'neutral' | 'active' | 'connected' | 'suggested' | 'available'

// Filtres pour le lettrage
export interface LettrageFilters {
  residence_id: string
  fiscal_year_id?: string
  account_id?: string
  show_lettered?: boolean
  owner_id?: string
}

// ===== MODULE FOURNISSEURS =====
export * from './suppliers'
